import React,{useEffect,useCallback} from 'react'

export const useAutoScroll = (ref, dependencies) => {
  const scrollToBottom = useCallback(() => {
    if (ref.current) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [ref]);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom, ...dependencies]);
};


// User prompt topic text
export const formatTopicText = (text) => {
  // Check if text starts with specific keywords followed by colon and space
  const patterns = [
    /^(Product:\s)/i,
    /^(Topic:\s)/i, 
    /^(Country:\s)/i
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const keywordWithColon = match[1];
      const restOfText = text.substring(keywordWithColon.length);
      
      return (
        <>
          <span style={{ color: '#54565B', fontWeight: 600 }}>
            {keywordWithColon.trim()}
          </span>
          <span>{restOfText}</span>
        </>
      );
    }
  }
  
  // For all other cases, return normal text
  return text;
};

//botmsg text 
export const formatBotMessage = (text) => {
   text = text.replace(/(\s*)(\d+\.\s)/g, '\n$2');
   
  const lines = text.split('\n');
  
  const nonEmptyLines = [];
  
  // Check if this is a topic correction response
  const isTopicCorrection = text.toLowerCase().includes('provide the correct topic name');
  
  // Enhanced summary detection
  let isSummarySection = false;
  let summaryItemCount = 0;
  
  // Count summary items in the text
  const summaryItems = ['Product', 'Country', 'Topic', 'Communication Objective'];
  summaryItems.forEach(item => {
    if (text.includes(`${item}:`) || text.includes(`**${item}:**`) || text.includes(`${item}::`) || text.includes(`${item}::**`)) {
      summaryItemCount++;
    }
  });
  
  // Consider it a summary section if we have at least 2 summary items
  isSummarySection = summaryItemCount >= 2 || text.includes('Summary of your selection');
  
  // First pass: filter out consecutive empty lines, keeping only one
  let previousWasEmpty = false;
  for (const line of lines) {
    const isEmpty = line.trim() === '';
    
    if (isEmpty) {
      if (!previousWasEmpty && !isTopicCorrection) {
        nonEmptyLines.push(''); // Add only one empty line as separator
        previousWasEmpty = true;
      }
    } else {
      nonEmptyLines.push(line);
      previousWasEmpty = false;
    }
  }
  
  // Helper function to replace **text** with <strong>text</strong>
  const formatBoldText = (text) => {
    if (!text) return text;
    
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // This is a bold section, remove ** and wrap in strong
        const boldText = part.substring(2, part.length - 2);
        return <strong key={partIndex}>{boldText}</strong>;
      }
      return part;
    });
  };
  
  // NEW: Function to clean checkmarks from text
  const cleanCheckmarks = (text) => {
    if (!text) return text;
    // Remove ✓ and any surrounding spaces
    return text.replace(/\s*✓\s*/g, ' ').trim();
  };
  
  return nonEmptyLines.map((line, index) => {
    const isFirstLine = index === 0;
    const isLastLine = index === nonEmptyLines.length - 1;
    const isEmptyLine = line === '';
    
    // Clean checkmarks from line before processing
    const cleanedLine = cleanCheckmarks(line);
    
    // If line contains **bold**, format it
    if (/\*\*(.*?)\*\*/.test(cleanedLine)) {
      return (
        <span key={index}>
          {formatBoldText(cleanedLine)}
          {!isLastLine && <br />}
        </span>
      );
    }

    if (isEmptyLine) {
      return isTopicCorrection ? null : <br key={index} />;
    }
    
    // Check if this is a summary line with :: or ::**
    const isSummaryLine = (cleanedLine.includes('::') || cleanedLine.includes(':**') || cleanedLine.includes('::**')) && 
                         (cleanedLine.includes('Product') || cleanedLine.includes('Country') || cleanedLine.includes('Topic') || cleanedLine.includes('Communication Objective'));
    
    // Handle summary lines with :: or ::**
    if (isSummaryLine) {
      // Remove all ** from the line for processing
      const cleanLine = cleanedLine.replace(/\*\*/g, '');
      
      // Handle both single : and double :: by replacing :: with :
      const normalizedLine = cleanLine.replace(/::/g, ':');
      const colonIndex = normalizedLine.indexOf(':');
      
      if (colonIndex !== -1) {
        const before = normalizedLine.substring(0, colonIndex).trim();
        const after = normalizedLine.substring(colonIndex + 1).trim();
        
        // Apply color ONLY to summary labels
        if (isSummarySection && summaryItems.includes(before)) {
          return (
            <span key={index}>
              <span style={{ color: '#54565B', fontWeight: 500 }}>{before}:</span> <strong>{after}</strong>
              {!isLastLine && <br />}
            </span>
          );
        }
        
        return (
          <span key={index}>
            {before}: <strong>{after}</strong>
            {!isLastLine && <br />}
          </span>
        );
      }
    }
    
    // Handle lines starting with ** (like **Summary of your selection : **)
    if (cleanedLine.trim().startsWith('**')) {
      // Remove ** from the line for display
      const cleanText = cleanedLine.replace(/\*\*/g, '');
      return (
        <span key={index}>
          <strong>{cleanText}</strong>
          {!isLastLine && <br />}
        </span>
      );
    }
    
    // Format lines with single colon (not summary lines)
    if (cleanedLine.includes(':') && cleanedLine.split(':')[1]?.trim() && !isSummaryLine) {
      const colonIndex = cleanedLine.indexOf(':');
      const before = cleanedLine.substring(0, colonIndex);
      const after = cleanedLine.substring(colonIndex + 1);
      
      // Check if before part has ** at the end (like "Product:**")
      const isBeforeBold = before.trim().endsWith('**');
      const displayBefore = isBeforeBold ? before.replace(/\*\*/g, '') : before;
      
      return (
        <span key={index}>
          {isBeforeBold ? <strong>{displayBefore.trim()}</strong> : displayBefore.trim()}:
          {formatBoldText(after)}
          {!isLastLine && <br />}
        </span>
      );
    }
    
    // Split long sentences after 2 sentences
    const sentences = cleanedLine.split(/[.!?]+/).filter(s => s.trim().length > 0);
    if (sentences.length > 2) {
      const firstTwoSentences = sentences.slice(0, 2).join('. ') + '.';
      const remainingSentences = sentences.slice(2).join('. ') + (cleanedLine.endsWith('.') ? '.' : '');
      
      return (
        <span key={index}>
          <strong>{formatBoldText(firstTwoSentences)}</strong>
          <br />
          {formatBoldText(remainingSentences)}
          {!isLastLine && <br />}
        </span>
      );
    }
    
    // For topic correction responses: first line only bold, no spaces
    if (isTopicCorrection) {
      if (isFirstLine) {
        return (
          <span key={index}>
            <strong>{formatBoldText(cleanedLine)}</strong>
            {!isLastLine && <br />}
          </span>
        );
      }
      // All other lines normal for topic correction
      return (
        <span key={index}>
          {formatBoldText(cleanedLine)}
          {!isLastLine && <br />}
        </span>
      );
    }
    
    // First line bold (if it's a short message) - for non-topic correction
    if (isFirstLine) {
      return (
        <span key={index}>
          <strong>{formatBoldText(cleanedLine)}</strong>
          {!isLastLine && <br />}
        </span>
      );
    }
    
    // Confirmation questions bold
    if (cleanedLine.toLowerCase().includes('please confirm') || 
        cleanedLine.toLowerCase().includes('is this correct') ||
        cleanedLine.includes('?')) {
      return (
        <span key={index}>
          <strong>{formatBoldText(cleanedLine)}</strong>
          {!isLastLine && <br />}
        </span>
      );
    }
    
    // All other lines normal
    return (
      <span key={index}>
        {formatBoldText(cleanedLine)}
        {!isLastLine && <br />}
      </span>
    );
  }).filter(Boolean); // Remove null values
};





